import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { MenuItem } from "@mui/material";
import Button from "@mui/material/Button";

import DatagridTable from "../Components/Table/DataGrid_Table";
import { createColumn } from "../Components/Table/DataGrid_Cullums";
import { fetchReservations } from "../Redux/Reservations_Slice";

const SearchSchema = Yup.object().shape({
  reservationNumber: Yup.string(),
  status: Yup.string()
});

const columns = [
  createColumn("reservationNumber", "Buchungsnummer", 200),
  createColumn("payWay", "Zahlungsart", 150),
  createColumn("status", "Status", 200, "string", ({ value }) => {
    let color, label;
    if (value === "accepted" || value === "Angenommen") {
      color = "#B6E6C1";
      label = "Angenommen";
    } else if (value === "rejected" || value === "Abgelehnt") {
      color = "#E6B6B6";
      label = "Abgelehnt";
    } else {
      color = "#F0E0C0";
      label = "Ausstehend";
    }
    return (
      <span
        style={{
          backgroundColor: color,
          padding: "12px 18px",
          borderRadius: "10px",
          fontWeight: "bold"
        }}
      >
        {label}
      </span>
    );
  }),
  createColumn("isPaid", "Bezahlt", 120, "boolean"),
  createColumn("isCanceled", "Storniert", 120, "boolean"),
  createColumn("isAGift", "Geschenk", 120, "boolean"),
  createColumn("taxRate", "Mehrwertsteuer (%)", 120, "number"),
  createColumn("taxPrice", "Steuerbetrag (€)", 120, "number"),
  createColumn("totalExtraObjectsPrice", "Zusatzobjekte (€)", 120, "number"),
  createColumn("totalPrice", "Gesamtpreis (€)", 120, "number"),
];

const Rechnungen = () => {
  const dispatch = useDispatch();
  const { data, isLoading, error, pagination } = useSelector(
    (state) => state.Reservations
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [searchFilters, setSearchFilters] = useState({});

  useEffect(() => {
    dispatch(
      fetchReservations({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: searchFilters
      })
    );
  }, [dispatch, paginationModel, searchFilters]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSearch = (values) => {
    const filters = {};
    if (values.reservationNumber) filters.reservationNumber = values.reservationNumber;
    if (values.status) filters.status = values.status;
    setSearchFilters(filters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleReset = (resetForm) => {
    resetForm();
    setSearchFilters({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const Toolbar = () => (
    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, bgcolor: "#D6B981" }}>
      <Formik
        initialValues={{ 
          reservationNumber: "", 
          status: "" 
        }}
        validationSchema={SearchSchema}
        onSubmit={handleSearch}
      >
        {({ errors, touched, resetForm }) => (
          <Form style={{ display: "flex", alignItems: "center", gap: 16, width: "100%", justifyContent: "end" }}>
            <Field
              as={TextField}
              name="reservationNumber"
              label="Buchungsnummer"
              variant="outlined"
              size="small"
              error={touched.reservationNumber && Boolean(errors.reservationNumber)}
              helperText={touched.reservationNumber && errors.reservationNumber}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Field
              as={TextField}
              select
              name="status"
              label="Status"
              variant="outlined"
              size="small"
              error={touched.status && Boolean(errors.status)}
              helperText={touched.status && errors.status}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="accepted">Angenommen</MenuItem>
              <MenuItem value="rejected">Abgelehnt</MenuItem>
              <MenuItem value="pending">Ausstehend</MenuItem>
            </Field>
            <Button type="submit" variant="contained" color="inherit" size="medium">
              Suchen
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="medium"
              onClick={() => handleReset(resetForm)}
            >
              Zurücksetzen
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Fehler beim Laden der Reservierungen: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <DatagridTable
        columns={columns}
        rows={data || []} 
        loading={isLoading}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        rowCount={pagination?.total || 0} 
        pageSizeOptions={[5, 10, 25]}
        toolbarComponent={<Toolbar />}
      />
    </Box>
  );
};

export default Rechnungen;